import React from 'react';
import SwitchDialogWindows from 'components/shared/SwitchDialogWindows';
import { useSelector } from 'react-redux';
import { RootStore } from 'store/types';
import { FullName, IParty } from 'ui-toolkit/orders/interfaces/IParty';
import { Grid, GridItem } from 'ui-toolkit/components/Grid';
import { BodyText, BoldBodyText, Header3Text } from 'ui-toolkit/components/Typography/basicTypography';
import { SpruceHelpInlineLink } from 'ui-toolkit/components/Navigation/SpruceHelpInlineLink';
import { ModalWindow } from 'ui-toolkit/components/ModalWindow/ModalWindow';
import { Button } from 'ui-toolkit/components/Button/Button';
import { useOnDeleteParty } from './useOnDeleteParty';

interface IDeletePartyModalProps {
  party: IParty;
}

export const DeletePartyModal: React.FC<IDeletePartyModalProps> = ({ party }) => {
  const partyAction = useSelector((state: RootStore) => state.order.partiesActionsSlice);
  const { onCloseClick } = useOnDeleteParty(party);

  return (
    <SwitchDialogWindows
      updating={partyAction.fetchStatus.fetching}
      processingTitle={'Working...'}
      updated={partyAction.fetchStatus.fetched}
      error={partyAction.fetchError?.message}
      successTitle={''}
      successMessage={<SuccessMessage />}
      errorTitle={
        <Header3Text>
          Party was not deleted. Please <SpruceHelpInlineLink /> to submit a request for help.
        </Header3Text>
      }
      onCancelClick={onCloseClick}
      defaultDialogComponent={<DefaultMessage party={party} />}
    />
  );
};

const SuccessMessage = () => {
  return (
    <Grid textAlign={'center'}>
      <Header3Text>Party has been deleted!</Header3Text>
      <div>
        <BodyText>Documents have regenerated.</BodyText>
        <BoldBodyText>Note: A new title search may be required.</BoldBodyText>
      </div>
    </Grid>
  );
};

const DefaultMessage: React.FC<{ party: IParty }> = ({ party }) => {
  const { onDeleteParty, onClose, onCloseClick } = useOnDeleteParty(party);

  return (
    <ModalWindow uitype={'fit'} title={`Delete ${FullName(party)}`} onClose={onCloseClick}>
      <Grid columnCount={4}>
        <GridItem columnStart={`span 4`}>
          <BodyText>Are you sure you want to delete {FullName(party)} from this order?</BodyText>
        </GridItem>
        <Button intent={'danger'} onClick={onDeleteParty} data-test-id={`confirm-delete`}>
          Delete
        </Button>
        <Button intent="ghost" isInline onClick={onClose}>
          Cancel
        </Button>
      </Grid>
    </ModalWindow>
  );
};
